/**
 * GPU 사용량의 기간 프리셋이다. 화면은 프리셋 키만 가지고, 요청 시점에 절대 시각으로 바꾼다.
 *
 * 기간은 항상 있어야 한다. 그래서 다른 화면의 상대 기간 축과 달리 "전체"가 없다.
 */
export const GPU_USAGE_PERIOD_OPTIONS = [
  { value: "7d", label: "최근 1주", days: 7 },
  { value: "14d", label: "최근 2주", days: 14 },
  { value: "30d", label: "최근 한 달", days: 30 },
] as const;

export type GpuUsagePeriod = (typeof GPU_USAGE_PERIOD_OPTIONS)[number]["value"];

export function gpuUsagePeriodLabel(period: GpuUsagePeriod): string {
  return GPU_USAGE_PERIOD_OPTIONS.find((option) => option.value === period)?.label ?? period;
}

const dayMs = 24 * 60 * 60 * 1000;
const minuteMs = 60 * 1000;

/**
 * 프리셋 → 서버에 보낼 절대 기간(aware UTC ISO). `nowMs` 는 테스트가 고정할 수 있도록 인자로 받는다.
 *
 * 서버의 버킷은 UTC 일이다. 그래서 시작을 UTC 자정에 맞춘다. 그러면 첫 일이 온전한 하루가 되고,
 * 마지막 일(오늘)만 부분이다. 기간은 `오늘-(N-1)일 ~ 지금` 이라서 "1주"는 오늘을 포함한 UTC 일
 * 7개다. UTC 에는 DST 가 없다. 그래서 ms 를 빼도 날짜가 틀리지 않는다.
 *
 * 끝은 분 단위로 내린다. 상류는 `start`·`end` 문자열로 응답을 캐시한다. 그래서 같은 분 안의 요청은
 * 같은 캐시 항목을 쓴다.
 */
export function gpuUsageWindow(
  period: GpuUsagePeriod,
  nowMs: number,
): { start: string; end: string } {
  const days = GPU_USAGE_PERIOD_OPTIONS.find((option) => option.value === period)?.days ?? 1;
  const now = new Date(nowMs);
  const todayUtcMs = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  const startMs = todayUtcMs - (days - 1) * dayMs;
  // UTC 자정 직후 1분 안에는 분 단위로 내린 끝이 시작과 같아진다. 서버는 `end <= start` 를
  // 거절한다. 그래서 끝을 시작보다 최소 1분 뒤에 둔다.
  const endMs = Math.max(Math.floor(nowMs / minuteMs) * minuteMs, startMs + minuteMs);
  return { start: new Date(startMs).toISOString(), end: new Date(endMs).toISOString() };
}
